import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

export const DRILLDOWN_METRICS = {
  TOTAL_INVESTORS: 'total_investors',
  ACTIVE_INVESTORS: 'active_investors',
  TOTAL_PARTNERS: 'total_partners',
  ACTIVE_PARTNERS: 'active_partners',
};

const METRIC_LABELS = {
  [DRILLDOWN_METRICS.TOTAL_INVESTORS]: 'Total investors',
  [DRILLDOWN_METRICS.ACTIVE_INVESTORS]: 'Active investors',
  [DRILLDOWN_METRICS.TOTAL_PARTNERS]: 'Total partners',
  [DRILLDOWN_METRICS.ACTIVE_PARTNERS]: 'Active partners',
};

const INVESTOR_METRICS = new Set([
  DRILLDOWN_METRICS.TOTAL_INVESTORS,
  DRILLDOWN_METRICS.ACTIVE_INVESTORS,
]);

const PARTNER_METRICS = new Set([
  DRILLDOWN_METRICS.TOTAL_PARTNERS,
  DRILLDOWN_METRICS.ACTIVE_PARTNERS,
]);

const ACTIVE_METRICS = new Set([
  DRILLDOWN_METRICS.ACTIVE_INVESTORS,
  DRILLDOWN_METRICS.ACTIVE_PARTNERS,
]);

export function isDrilldownMetric(value) {
  return Object.values(DRILLDOWN_METRICS).includes(value);
}

export function getDrilldownMetricLabel(metric) {
  return METRIC_LABELS[metric] || metric;
}

export function getDrilldownListPath(metric) {
  if (INVESTOR_METRICS.has(metric)) return '/investors/list';
  if (PARTNER_METRICS.has(metric)) return '/partners/list';
  return null;
}

/** Build list URL query from dashboard date range + KPI metric + optional RM scope. */
export function buildDashboardDrilldownSearch({ startDate, endDate, metric, scopeUserId }) {
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (metric) params.set('metric', metric);
  if (scopeUserId && scopeUserId !== 'all') params.set('rm', scopeUserId);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function buildDashboardDrilldownUrl({ path, startDate, endDate, metric, scopeUserId }) {
  return `${path}${buildDashboardDrilldownSearch({ startDate, endDate, metric, scopeUserId })}`;
}

function parseDateParam(value) {
  if (!value) return null;
  const d = dayjs(value, 'YYYY-MM-DD', true);
  return d.isValid() ? d : null;
}

/** Match list pages: startOf/endOf day ISO strings used in API calls. */
export function drilldownDatesToFilterStrings(startDay, endDay) {
  if (!startDay || !endDay) {
    return { start_date: '', end_date: '' };
  }
  return {
    start_date: startDay.startOf('day').format(),
    end_date: endDay.endOf('day').format(),
  };
}

/**
 * Parse dashboard drill-down query params for list pages.
 * @returns {{ metric: string|null, activeInPeriod: boolean, dateRange: [dayjs, dayjs]|null, filterDates: { start_date, end_date }, bannerLabel: string|null }}
 */
export function parseDashboardDrilldownSearchParams(searchParams) {
  const metric = searchParams.get('metric');
  const start = parseDateParam(searchParams.get('start_date'));
  const end = parseDateParam(searchParams.get('end_date'));

  const validMetric = isDrilldownMetric(metric) ? metric : null;
  const activeInPeriod = validMetric && ACTIVE_METRICS.has(validMetric);

  let dateRange = null;
  let filterDates = { start_date: '', end_date: '' };

  if (start && end) {
    dateRange = [start, end];
    if (!activeInPeriod) {
      filterDates = drilldownDatesToFilterStrings(start, end);
    }
  }

  const rmId = searchParams.get('rm') || '';

  let bannerLabel = null;
  if (validMetric && start && end) {
    const rangeLabel = `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
    bannerLabel = `Filtered from dashboard: ${getDrilldownMetricLabel(validMetric)}, ${rangeLabel}`;
    if (rmId) bannerLabel += ', RM filter applied';
  }

  return {
    metric: validMetric,
    activeInPeriod,
    dateRange,
    filterDates,
    bannerLabel,
    apiParams:
      start && end
        ? {
            start_date: start.format('YYYY-MM-DD'),
            end_date: end.format('YYYY-MM-DD'),
            ...(activeInPeriod ? { active_in_period: true } : {}),
          }
        : {},
  };
}

export function useDashboardDrilldownFilters() {
  const [searchParams] = useSearchParams();
  return useMemo(
    () => parseDashboardDrilldownSearchParams(searchParams),
    [searchParams]
  );
}
